import {Component, input, output, OnInit, inject, computed, effect} from '@angular/core';
import {VVPMActivity} from "../../models/vvpm-activity.model";
import {
  RdsButtonModule,
  RdsDividerModule,
  RdsFormFieldModule,
  RdsIconComponent,
  RdsSwitchModule,
} from "@rds/angular-components";

import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { UserService } from '../../services/user.service';
import { User } from '../../models/user.model';
import { StrategicPillar } from '../../services/strategic-pillar.service';
import { StrategicPillarSelectorComponent } from '../../shared/strategic-pillar-selector/strategic-pillar-selector.component';
import { ExpandableInputComponent } from '../expandable-input/expandable-input.component';

type VVPMDetailViewForm = FormGroup<{
  isVisible: FormControl<boolean>;
  name: FormControl<string | null>;
  filename: FormControl<string | null>;
}>;

@Component({
  selector: 'app-vvpm-activity-detail-view',
  standalone: true,
  imports: [
    RdsButtonModule,
    RdsDividerModule,
    RdsFormFieldModule,
    RdsIconComponent,
    RdsSwitchModule,
    ReactiveFormsModule,
    StrategicPillarSelectorComponent,
    ExpandableInputComponent
],
  templateUrl: './vvpm-activity-detail-view.component.html',
  styleUrl: './vvpm-activity-detail-view.component.scss'
})
export class VVPMActivityDetailViewComponent implements OnInit {
  // Raw input - might lose prototype methods
  vvpmActivityInput = input.required<VVPMActivity>({ alias: 'vvpmActivity' });
  
  // Computed signal that ensures we have a proper VVPMActivity instance with methods
  vvpmActivity = computed(() => {
    const input = this.vvpmActivityInput();
    // If it's already a proper instance with methods, return it
    if (input && typeof input.getCombinedAudiences === 'function') {
      return input;
    }
    // Otherwise, reconstruct the instance to restore prototype methods
    return Object.assign(new VVPMActivity(), input);
  });
  
  isLoadingChanges = input.required<boolean>();
  updateVVPMActivity = output<{data: VVPMActivity, strategicPillarIds: number[]}>();
  
  private readonly formBuilder = inject(FormBuilder);
  private readonly userService = inject(UserService);
  
  form!: VVPMDetailViewForm;
  isViewer = false;
  pendingStrategicPillars: { pillarIds: number[], pillars: StrategicPillar[] } | null = null;

  constructor() {
    effect(() => {
      const v = this.vvpmActivity();
      if (this.form) {
        this.form.controls.name.setValue(v.name__v, { emitEvent: false });
        this.form.controls.filename.setValue(v.filename__v, { emitEvent: false });
        this.form.controls.isVisible.setValue(v.isVisible ?? true, { emitEvent: false });
      }
    });
  }

  ngOnInit() {
    // Check user role first
    this.userService.getUserData().then((user: User) => {
      this.isViewer = user.isViewer;
      this.setupForm();
    });
  }

  private setupForm() {
    const v = this.vvpmActivity();
    this.form = this.formBuilder.group({
      isVisible: this.formBuilder.control<boolean>({ value: v.isVisible ?? true, disabled: this.isViewer }, { nonNullable: true }),
      name: this.formBuilder.control<string | null>({ value: v.name__v, disabled: true }, { nonNullable: false }),
      filename: this.formBuilder.control<string | null>({ value: v.filename__v, disabled: true }, { nonNullable: false }),
    });
  }

  onSubmit() {
    if (this.isViewer) return;

    const copy = Object.assign(new VVPMActivity(), JSON.parse(JSON.stringify(this.vvpmActivity())));
    copy.isVisible = this.form.controls.isVisible.value;

    const existingPillarIDs = (this.vvpmActivity().strategicPillars || []).map(p => p.id);

    this.updateVVPMActivity.emit({
      data: copy,
      strategicPillarIds: this.pendingStrategicPillars ? this.pendingStrategicPillars.pillarIds : existingPillarIDs
    });
  }

  onPillarChanged(selection: { pillarIds: number[], pillars: StrategicPillar[] }) {
    // Store the pending change for when user clicks Save
    this.pendingStrategicPillars = selection;
    console.log('Strategic pillar selection changed:', selection);
  }

  getCombinedAudiences(): string[] {
    const activity = this.vvpmActivity();
    if (activity && typeof activity.getCombinedAudiences === 'function') {
      return activity.getCombinedAudiences();
    }
    return [];
  }

  openVeevaVault() {
    const url = this.vvpmActivity().veevaVaultLink;
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    
    // Fallback for older browsers that don't support the features parameter
    if (newWindow) {
      newWindow.opener = null;
    }
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
}
